import { TemplateResult, CSSResult, LitElement, html, PropertyValues } from 'lit';

export class LitPage extends LitElement {
  private _slotObserver: MutationObserver | null = null;

  styles(): CSSResult | undefined {
    return undefined;
  }

  content() : TemplateResult<1> | undefined {
    return undefined;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this._slotObserver = new MutationObserver(() => this.distributeChildren());
    this._slotObserver.observe(this, { childList: true });
  }

  disconnectedCallback() {
    this._slotObserver?.disconnect();
    this._slotObserver = null;
    super.disconnectedCallback();
  }

  protected updated(changed: PropertyValues): void {
    this.distributeChildren();
    super.updated(changed);
  }

  // Light-DOM slotting: <slot> elements are inert outside a shadow root, so we
  // distribute the host's light-DOM children into their matching <slot> targets
  // ourselves on every update. A direct child is "rendered content" (and left
  // alone) when it contains one of this component's own slots; everything else
  // that is still a direct child is treated as slotted content — including
  // consumer text nodes (e.g. `<luna-button>Play</luna-button>`), which
  // `this.children` would miss. Lit's comment marker nodes always stay put.
  private distributeChildren(): void {
    const slots = this.ownSlots();
    if (slots.length === 0) {
      return;
    }

    const defaultSlot = slots.find((slot) => !slot.hasAttribute('name'));

    for (const child of Array.from(this.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        // Move only real consumer text (e.g. a bare `Play` label). Whitespace
        // formatting nodes stay put — they are part of Lit's template DOM and
        // moving them breaks part boundaries on the next render.
        if (child.textContent?.trim()) {
          defaultSlot?.appendChild(child);
        }
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }

      const element = child as Element;
      if (element.tagName === 'STYLE') {
        continue;
      }
      if (slots.some((slot) => element.contains(slot))) {
        continue;
      }

      const slotName = element.getAttribute('slot');
      const target = slotName
        ? slots.find((slot) => slot.getAttribute('name') === slotName)
        : defaultSlot;
      if (target) {
        target.appendChild(element);
      }
    }
  }

  // The <slot> elements rendered by this component's own content() — excluding
  // any slots that belong to nested custom-element children.
  private ownSlots(): Element[] {
    return Array.from(this.querySelectorAll('slot')).filter((slot) => {
      let ancestor: Element | null = slot.parentElement;
      while (ancestor && ancestor !== this) {
        if (ancestor.tagName.includes('-')) {
          return false;
        }
        ancestor = ancestor.parentElement;
      }
      return ancestor === this;
    });
  }

  render() {
    return html`
      <style>${this.styles()}</style>
      ${this.content()}
    `;
  }
}
